import React, { useState, useEffect } from 'react';
import { format, parse, isValid } from 'date-fns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import toast from 'react-hot-toast';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Switch,
    FormControlLabel,
    Box,
    Typography,
    Divider,
    CircularProgress,
    Chip,
    Collapse,
    IconButton,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import api from 'state/axios';


const parseTimeStr = (str) => {
    if (!str) return null;
    const d = parse(str, 'HH:mm', new Date());
    return isValid(d) ? d : null;
};

const formatTimeStr = (date) => (date && isValid(date) ? format(date, 'HH:mm') : '');

const AssignmentDialog = ({ bar, onClose, onSaved, canEdit = false, employeeMap = new Map() }) => {
    const assignment = bar?.rawAssignment;
    const shift = assignment?.shiftId;

    const [form, setForm] = useState({
        startTime: '',
        endTime: '',
        workingTime: '',
        memo: '',
        isLocked: false,
        requiredEmployees: 1,
    });
    const [saving, setSaving] = useState(false);
    const [showAssignees, setShowAssignees] = useState(false);

    useEffect(() => {
        if (!bar) return;
        setForm({
            startTime: bar.startTime || '',
            endTime: bar.endTime || '',
            workingTime: assignment?.workingTime ?? '',
            memo: assignment?.memo || '',
            isLocked: assignment?.isLocked || false,
            requiredEmployees: bar?.requiredEmployees ?? assignment?.requiredEmployees ?? 1,
        });
        setShowAssignees(false);
    }, [bar, assignment]);

    const handleTimeChange = (field) => (date) => {
        setForm(prev => ({ ...prev, [field]: formatTimeStr(date) }));
    };

    const handleChange = (field) => (e) => {
        const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        if (!assignment?._id) return;
        setSaving(true);
        try {
            const workingTime = form.workingTime !== '' ? Number(form.workingTime) : 0;
            await api.put(`/assignments/update/${assignment._id}`, {
                startTime: form.startTime,
                endTime: form.endTime,
                workingTime,
                memo: form.memo,
                isLocked: form.isLocked,
                requiredEmployees: Number(form.requiredEmployees) || 1,
            });
            onSaved(assignment._id, {
                startTime: form.startTime,
                endTime: form.endTime,
                workingTime,
                memo: form.memo,
                isLocked: form.isLocked,
                requiredEmployees: Number(form.requiredEmployees) || 1,
            });
            toast.success('Assignment updated');
            onClose();
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Update failed');
        } finally {
            setSaving(false);
        }
    };

    if (!bar) return null;

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Dialog 
                open={Boolean(bar)} 
                onClose={onClose} 
                maxWidth="xs" 
                fullWidth
            >
                <DialogTitle>Edit Assignment</DialogTitle>
                <Divider />
                <DialogContent>
                    <Box display="flex" flexDirection="column" gap={2}>
                        {/* Read-only info */}
                        <Box display="flex" gap={2}>
                            <TextField
                                label="Shift"
                                value={shift?.shiftName || shift?.shiftCode || ''}
                                size="small"
                                fullWidth
                                slotProps={{ input: { readOnly: true } }}
                            />
                            <TextField
                                label="Date"
                                value={assignment?.date ? format(new Date(assignment.date), 'dd/MM/yyyy') : ''}
                                size="small"
                                fullWidth
                                slotProps={{ input: { readOnly: true } }}
                            />
                        </Box>

                        <Divider />

                        {/* Editable times */}
                        <Box display="flex" gap={2}>
                            <TimePicker
                                label="Start Time"
                                value={parseTimeStr(form.startTime)}
                                onChange={handleTimeChange('startTime')}
                                ampm={false}
                                readOnly={!canEdit}
                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                            />
                            <TimePicker
                                label="End Time"
                                value={parseTimeStr(form.endTime)}
                                onChange={handleTimeChange('endTime')}
                                ampm={false}
                                readOnly={!canEdit}
                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                            />
                        </Box>

                        <Box display="flex" gap={2}>
                            <TextField
                                label="Working Hours"
                                type="number"
                                value={form.workingTime}
                                onChange={handleChange('workingTime')}
                                size="small"
                                fullWidth
                                slotProps={{ input: { readOnly: !canEdit, inputProps: { min: 0, max: 24, step: 0.5 } } }}
                            />

                            <TextField
                                label="Required Employees"
                                type="number"
                                value={form.requiredEmployees}
                                onChange={handleChange('requiredEmployees')}
                                size="small"
                                fullWidth
                                slotProps={{ input: { readOnly: !canEdit, inputProps: { min: 1, max: 99 } } }}
                            />
                        </Box>

                        <TextField
                            label="Memo"
                            value={form.memo}
                            onChange={handleChange('memo')}
                            size="small"
                            fullWidth
                            multiline
                            minRows={2}
                            slotProps={{ input: { readOnly: !canEdit } }}
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={form.isLocked}
                                    onChange={handleChange('isLocked')}
                                    size="small"
                                    disabled={!canEdit}
                                />
                            }
                            label={<Typography variant="body2">Locked</Typography>}
                        />

                        <Divider />

                        {/* Assignees section */}
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ cursor: 'pointer', userSelect: 'none' }}
                            onClick={() => setShowAssignees(v => !v)}
                        >
                            <Typography variant="body2" color="text.secondary">
                                Assignees:&nbsp;
                                <strong>{assignment?.assignees?.length ?? 0}</strong>
                                &nbsp;/&nbsp;
                                <strong>{form.requiredEmployees}</strong>
                            </Typography>
                            <IconButton size="small" tabIndex={-1}>
                                {showAssignees ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                            </IconButton>
                        </Box>
                        <Collapse in={showAssignees} unmountOnExit>
                            <Box display="flex" flexWrap="wrap" gap={0.5}>
                                {assignment?.assignees?.length > 0 &&
                                    assignment.assignees.map(ae => {
                                        const id = String(ae.employeeId?._id ?? ae.employeeId);
                                        const emp = employeeMap.get(id);
                                        return (
                                            <Chip
                                                key={id}
                                                size="small"
                                                label={emp?.employeeCode ?? id}
                                                title={emp?.label}
                                                variant="outlined"
                                                sx={{ fontSize: '0.7rem' }}
                                            />
                                        );
                                    })
                                }
                            </Box>
                        </Collapse>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="outlined"
                        color="secondary"
                        disabled={saving}
                        onClick={onClose}
                    >
                        Cancel
                    </Button>
                    {canEdit && (
                        <Button
                            variant="outlined"
                            disabled={saving}
                            startIcon={saving ? <CircularProgress size={14} /> : null}
                            onClick={handleSave}
                        >
                            Save
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </LocalizationProvider>
    );
};

export default AssignmentDialog;
